import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogSheetContent, DialogSheetHeader, DialogSheetBody, DialogSheetFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon } from 'lucide-react';
import { createTransaction, updateTransaction, contributeToGoal } from '../lib/api';
import { warnIfPossibleDuplicate } from '../lib/duplicates';
import { useLocalTransactions } from '../context/LocalTransactionsContext';
import { getIcon, catColor, catTint } from '../lib/constants';
import ReceiptScanButton from './ReceiptScan';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Redesigned per explicit feedback, modeled on how Apple's own modals (e.g.
// Calendar's "New Event") are laid out: near-full-screen, a header bar with
// Cancel/Save always visible (not a button buried at the bottom of a
// scrollable form — the original complaint was the category dropdown could
// push/cover it), the amount focused automatically with no action needed,
// and a real icon grid for category instead of a cramped dropdown.
// `prefill` (optional) — form values from a scanned receipt (see
// ReceiptScan.js), opened for review; saved with source "ocr".
// `goals` (optional) — when present, a third "Meta" type records a goal
// contribution (aporte) instead of an income/expense.
// "3,45" / "3.456" / "$3a" → "3.45": digits plus one decimal separator (comma
// or dot, normalized to dot), at most two decimals.
function sanitizeAmount(value) {
  const v = String(value).replace(',', '.').replace(/[^\d.]/g, '');
  const [whole, ...rest] = v.split('.');
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
}

// Most-used categories first (GET /categories → use_count / last_used_at over
// the last 90 days); unused ones keep their original order at the end.
function sortByUse(cats, enabled) {
  if (!enabled) return cats;
  return cats
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (b.c.use_count || 0) - (a.c.use_count || 0)
      || String(b.c.last_used_at || '').localeCompare(String(a.c.last_used_at || ''))
      || a.i - b.i)
    .map(x => x.c);
}

export default function TransactionDialog({ open, onOpenChange, categories, editTransaction, onSuccess, prefill, goals = [] }) {
  const local = useLocalTransactions();
  const categoriesRef = useRef(null);
  const [nudgeCategory, setNudgeCategory] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    type: 'expense',
    category_id: '',
    description: '',
    date: new Date()
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanned, setScanned] = useState(false);
  const amountRef = useRef(null);

  useEffect(() => {
    if (editTransaction) {
      setForm({
        amount: String(editTransaction.amount),
        type: editTransaction.type,
        category_id: editTransaction.category_id,
        description: editTransaction.description || '',
        date: new Date(editTransaction.date)
      });
    } else if (prefill) {
      setForm(prefill);
    } else {
      setForm({ amount: '', type: 'expense', category_id: '', description: '', date: new Date() });
    }
    setScanned(!editTransaction && !!prefill);
  }, [editTransaction, open, prefill]);

  const isGoal = form.type === 'goal';
  const filteredCategories = isGoal
    ? goals.map(g => ({ id: g.id, name: g.name, icon: g.icon, color: g.color }))
    : sortByUse(categories.filter(c => {
      if (form.type === 'income') return c.type === 'income';
      return c.type === 'expense' || c.type === 'both';
    }), !editTransaction);

  // No busy guard here previously — the single most-used action in the app
  // had zero visual difference between "click registered, working on it" and
  // "nothing happened," so a slow response (or just mobile network latency)
  // reads as a dead button. Worse: nothing stopped a second tap from firing
  // a second real request before the first resolved, which could create a
  // genuine duplicate transaction, not just a UI glitch.
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const data = {
      amount: parseFloat(String(form.amount).replace(',', '.')),
      type: form.type,
      category_id: form.category_id,
      description: form.description,
      date: form.date.toISOString(),
      ...(!editTransaction && scanned ? { source: 'ocr' } : {}),
    };

    // Optimistic: the sheet closes, the toast shows and the list changes right
    // away; the API call and the refetch (onSuccess) settle in the background.
    // On failure the refetch puts the list back and the error says so.
    const cat = categories.find(c => c.id === data.category_id);
    const shown = {
      ...data,
      category_name: cat?.name, category_icon: cat?.icon, category_color: cat?.color,
    };
    const tempId = `tmp-${Date.now()}`;
    onOpenChange(false);
    setSaving(false);
    try {
      if (isGoal) {
        toast.success('Aporte registrado');
        await contributeToGoal(form.category_id, {
          amount: data.amount, date: data.date, note: form.description.trim(),
        });
      } else if (editTransaction) {
        local.upsert({ ...editTransaction, ...shown });
        toast.success('Transaccion actualizada');
        await updateTransaction(editTransaction.id, data);
      } else {
        local.upsert({ ...shown, id: tempId });
        const created = await createTransaction(data);
        local.replace(tempId, created);
        if (!warnIfPossibleDuplicate(created, onSuccess)) toast.success('Transaccion creada');
      }
    } catch (e) {
      if (!editTransaction && !isGoal) local.remove(tempId);
      toast.error('No se pudo guardar. Intenta de nuevo.');
    } finally {
      onSuccess();
    }
  };

  const canSave = form.amount && parseFloat(form.amount) > 0 && form.category_id && !saving;
  const hasAmount = form.amount && parseFloat(form.amount) > 0;
  // The bottom button says what's still missing instead of just going grey.
  const saveLabel = !hasAmount ? 'Escribe el monto'
    : !form.category_id ? (isGoal ? 'Elige una meta' : 'Elige una categoría')
    : isGoal ? 'Guardar aporte' : form.type === 'income' ? 'Guardar ingreso' : 'Guardar gasto';

  // Enter / "listo" on the amount: save when everything's there; otherwise
  // close the keyboard and point at the category picker (it used to jump to
  // Descripción, and nothing said a category was required).
  const handleAmountKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (canSave) return handleSave();
    if (hasAmount && !form.category_id) {
      e.currentTarget.blur();
      setNudgeCategory(true);
      categoriesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => setNudgeCategory(false), 1600);
    }
  };
  const accent = form.type === 'income' ? 'emerald' : 'rose';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSheetContent
        data-testid="transaction-dialog"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          // A short delay lets the dialog's own enter transition/layout
          // settle first — focusing mid-transition can silently fail to
          // pop the mobile keyboard on some browsers.
          setTimeout(() => amountRef.current?.focus(), 50);
        }}
      >
        <DialogDescription className="sr-only">
          {editTransaction ? 'Modifica los datos de la transaccion' : 'Registra un ingreso o gasto'}
        </DialogDescription>

        <DialogSheetHeader
          title={editTransaction ? 'Editar transacción' : 'Nueva transacción'}
          onCancel={() => onOpenChange(false)}
          cancelTestId="transaction-cancel-btn"
          action={
            <button
              type="button"
              data-testid="save-transaction-btn"
              onClick={handleSave}
              disabled={!canSave}
              className={`text-[15px] font-semibold px-2 py-2 -mr-2 rounded-lg transition-colors active:scale-[0.97] disabled:opacity-40 ${
                accent === 'income' ? 'text-emerald-400' : 'text-violet-400'
              }`}
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
              ) : 'Guardar'}
            </button>
          }
        />

        <DialogSheetBody>
          {/* Type toggle — deliberately compact/secondary now; the amount
              below is the visual lead. */}
          <div className="flex gap-1 p-1 bg-slate-800/70 rounded-full w-fit mx-auto" data-testid="transaction-type-toggle">
            <button
              type="button"
              data-testid="type-expense-btn"
              onClick={() => setForm(p => ({ ...p, type: 'expense', category_id: '' }))}
              className={`hit-44 h-8 px-4 rounded-full text-xs font-semibold transition-all duration-200 ${
                form.type === 'expense' ? 'bg-rose-500/20 text-rose-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              Gasto
            </button>
            <button
              type="button"
              data-testid="type-income-btn"
              onClick={() => setForm(p => ({ ...p, type: 'income', category_id: '' }))}
              className={`hit-44 h-8 px-4 rounded-full text-xs font-semibold transition-all duration-200 ${
                form.type === 'income' ? 'bg-emerald-500/20 text-emerald-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              Ingreso
            </button>
            {!editTransaction && goals.length > 0 && (
              <button
                type="button"
                data-testid="type-goal-btn"
                onClick={() => setForm(p => ({ ...p, type: 'goal', category_id: '' }))}
                className={`hit-44 h-8 px-4 rounded-full text-xs font-semibold transition-all duration-200 ${
                  isGoal ? 'bg-violet-500/20 text-violet-300' : 'text-slate-400 hover:text-white'
                }`}
              >
                Meta
              </button>
            )}
          </div>

          {/* Receipt scan — fills the form below for review, never saves
              on its own. New transactions only. */}
          {!editTransaction && !isGoal && (
            <div className="flex justify-center">
              <ReceiptScanButton
                variant="pill"
                testId="dialog-receipt-scan-btn"
                onParsed={(values) => { setForm(values); setScanned(true); }}
              />
            </div>
          )}

          {/* Amount — the focal point. Auto-focused on open (see
              onOpenAutoFocus above), big, centered, minimal chrome. */}
          <div className="flex items-center justify-center gap-1">
            <span className={`text-3xl font-bold ${isGoal ? 'text-violet-400/60' : accent === 'income' ? 'text-emerald-500/60' : 'text-rose-500/60'}`}>$</span>
            <input
              ref={amountRef}
              data-testid="transaction-amount-input"
              // text + inputMode=decimal (not type=number, which overrides
              // it on Android with a keyboard showing "-" and "," and adds
              // spinner arrows): digits and one decimal separator only.
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.amount}
              onChange={e => setForm(p => ({ ...p, amount: sanitizeAmount(e.target.value) }))}
              onKeyDown={handleAmountKeyDown}
              enterKeyHint={form.category_id ? 'done' : 'next'}
              placeholder="0.00"
              className="bg-transparent border-none outline-none text-5xl font-bold text-white placeholder:text-slate-700 w-[9ch] text-center tabular-nums"
            />
          </div>

          {/* Category — a real icon grid instead of a dropdown: more space,
              centered, shows each category's own icon+color, and (since
              it's inline, not an overlay list) can never grow to cover the
              header's Guardar button the way the old dropdown could. */}
          <div ref={categoriesRef} className={`space-y-3 rounded-2xl transition-colors duration-300 ${nudgeCategory ? 'bg-violet-500/10' : ''}`}>
            <Label className={`text-center block transition-colors ${nudgeCategory ? 'text-violet-400' : 'text-slate-300'}`}>
              {isGoal ? 'Meta' : 'Categoría'}
            </Label>
            {isGoal && (
              <p className="text-xs text-slate-500 text-center -mt-1">Sale de tu balance, pero no cuenta como gasto.</p>
            )}
            {filteredCategories.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-4">No hay categorías de este tipo</p>
            ) : (
              // Two rows that scroll sideways once there are more than 8, so
              // a long list never pushes Descripción / Fecha off screen.
              <div className={`grid gap-2 ${filteredCategories.length > 8
                ? 'grid-rows-2 grid-flow-col auto-cols-[5.25rem] overflow-x-auto -mx-5 px-5 pb-1 snap-x'
                : 'grid-cols-4 justify-items-center'}`}
                data-testid="transaction-category-grid"
              >
                {filteredCategories.map(cat => {
                  const Icon = getIcon(cat.icon);
                  const selected = form.category_id === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      data-testid={`transaction-category-${cat.id}`}
                      onClick={() => setForm(p => ({ ...p, category_id: cat.id }))}
                      className={`w-full snap-start flex flex-col items-center gap-1.5 py-2 px-1 rounded-2xl border transition-all duration-200 active:scale-[0.97] ${
                        selected ? 'border-violet-500/50 bg-violet-500/10' : 'border-transparent hover:bg-white/5'
                      }`}
                    >
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center transition-colors"
                        style={{ backgroundColor: catTint(cat.color, selected ? 22 : 12) }}
                      >
                        <Icon className="w-5 h-5" style={{ color: catColor(cat.color) }} />
                      </div>
                      <span className={`text-xs font-medium text-center leading-tight truncate w-full ${selected ? 'text-white' : 'text-slate-400'}`}>
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="text-slate-300">Descripción (opcional)</Label>
            <Input
              data-testid="transaction-description-input"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Ej: Supermercado"
              className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
            />
            {/* Imported names are cleaned up (Fase 5); the bank's own text is
                kept so the user can still recognize the charge. */}
            {editTransaction?.description_raw && (
              <p className="text-xs text-slate-500" data-testid="transaction-description-raw">
                Texto del banco: {editTransaction.description_raw}
              </p>
            )}
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="text-slate-300">Fecha</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="transaction-date-btn"
                  className="w-full h-11 rounded-xl bg-slate-950/50 border-slate-700 text-white justify-start font-normal hover:bg-slate-800"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                  {format(form.date, "d 'de' MMMM, yyyy", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700 rounded-xl" align="start">
                <Calendar
                  mode="single"
                  selected={form.date}
                  onSelect={(d) => { if (d) { setForm(p => ({ ...p, date: d })); setCalendarOpen(false); } }}
                  className="rounded-xl"
                />
              </PopoverContent>
            </Popover>
          </div>
        </DialogSheetBody>

        {/* Big Guardar in the thumb zone; the header link stays as a shortcut. */}
        <DialogSheetFooter>
          <button
            type="button"
            data-testid="save-transaction-bottom-btn"
            onClick={handleSave}
            disabled={!canSave}
            className="w-full h-14 rounded-2xl bg-brand hover:bg-brand-hover text-on-brand text-base font-semibold flex items-center justify-center transition-colors active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            {saving
              ? <div className="w-5 h-5 border-2 border-on-brand/30 border-t-on-brand rounded-full animate-spin" />
              : saveLabel}
          </button>
        </DialogSheetFooter>
      </DialogSheetContent>
    </Dialog>
  );
}
